import { Box, Text, Heading, theme, Container } from '@everlywell/ui-kit';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { IGatsbyImage } from 'utils/types';

type SlotOverride = {
  sx?: any;
};

export type GridCardSlotOverrides = {
  root?: SlotOverride;
  imageContainer?: SlotOverride;
  image?: SlotOverride;
  title?: SlotOverride;
  description?: SlotOverride;
};

const mergeSlotSx = (
  ...sxValues: Array<Record<string, any> | undefined>
): Record<string, any> | undefined => {
  const merged = sxValues.reduce<Record<string, any>>((acc, sxValue) => {
    if (!sxValue) {
      return acc;
    }

    return {
      ...acc,
      ...sxValue,
    };
  }, {});

  return Object.keys(merged).length > 0 ? merged : undefined;
};

// Types ----------

export interface GridCardProps {
  image: IGatsbyImage;
  title: string;
  description: ContentfulRichText;
  imageStyles?: React.CSSProperties;
  slots?: GridCardSlotOverrides;
}

// GridCard ------

const GridCard: React.FC<GridCardProps> = (props) => {
  const { image, title, description, imageStyles, slots } = props;

  const containerSx = mergeSlotSx(slots?.root?.sx);
  const imageContainerSx = mergeSlotSx(slots?.imageContainer?.sx);
  const imageSx = mergeSlotSx(
    imageStyles as Record<string, any>,
    {
      '@media (max-width: 1400px)': {
        ...(imageStyles?.width ? {} : { width: '100% !important' }),
      },
    },
    slots?.image?.sx,
  );
  const titleSx = mergeSlotSx(slots?.title?.sx);
  const descriptionSx = mergeSlotSx(
    {
      '@media (max-width: 768px)': {
        maxWidth: '280px',
      },
    },
    slots?.description?.sx,
  );

  return (
    <Container size="fluid" sx={containerSx}>
      {/* Image aspect ratio to be 3:2 from Figma design */}
      <Box
        borderRadius={'xl'}
        display="flex"
        marginBottom={6}
        width="100%"
        sx={imageContainerSx}
      >
        {/* Image as a component for screen readers */}
        <Box
          borderRadius={'xl'}
          as={Img}
          image={image.gatsbyImageData}
          alt={title}
          objectFit="fill"
          sx={imageSx}
        />
      </Box>
      <Heading size="sm" marginBottom={theme.space[2]} sx={titleSx}>
        {title}
      </Heading>
      <Text size="lg" as="div" color="tints.darkGrey" sx={descriptionSx}>
        {renderRichText(description)}
      </Text>
    </Container>
  );
};

// Export ---------

export default React.memo(GridCard);
